import axios, { AxiosError } from "axios";
import { useMutation, useQueryClient } from "react-query";
import { STAC_URL, API_VERSION } from "utils/constants";
import { QUERY_KEYS } from "utils/requests";
import { defaultQueryableNames } from "utils/queryables";
import { PanelState } from "../types";
import { useEffect, useState } from "react";
import { IStacItem } from "types/stac";
import { initialEditorValue } from "../helpers";
import { ConfirmDialog } from "./Confirm";
import { Text } from "@fluentui/react";
import {
  ICollectionConfigUpdate,
  IMosaic,
  IMosaicRenderOption,
  IQueryableDefinition,
  ITileSettings,
} from "pages/Explore/types";
import { useGlobalToaster } from "components/controls/toastNotification";

export const useUpsertCollectionConfigOptions = (collectionId: string) => {
  const queryClient = useQueryClient();
  const { addToast } = useGlobalToaster();
  return useMutation(updateConfigs(collectionId), {
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.configManifest, collectionId]);
      queryClient.invalidateQueries([QUERY_KEYS.mosaics, collectionId]);
      queryClient.invalidateQueries([QUERY_KEYS.renderOptions, collectionId]);
      queryClient.invalidateQueries([QUERY_KEYS.tileSettings, collectionId]);
      queryClient.invalidateQueries([QUERY_KEYS.queryables, collectionId]);
      addToast(
        "Collection configuration updated",
        "Update successful",
        collectionId
      );
    },
    onError: (error: AxiosError) => {
      console.log("Mutation Error ", error);
      addToast(
        "Failed to update collection configuration",
        "Failed",
        undefined,
        "error",
        error.message + " " + JSON.stringify(error?.response?.data)
      );
    },
  });
};

const updateConfigs = (collectionId: string) => {
  return async (modifiedConfigs: ICollectionConfigUpdate) => {
    await updateMosaics(collectionId, modifiedConfigs);
    await updateRenderOptions(collectionId, modifiedConfigs);
    await updateQueryables(collectionId, modifiedConfigs);
    await updateTileSettings({
      collectionId,
      modifiedConfig: modifiedConfigs.tileSettings,
    });
  };
};

/**
 * A function that accepts modified mosaics and performs the update.
 * @param collectionId  - The ID of the collection.
 */
export const updateMosaics = async (
  collectionId: string,
  modifiedConfig: ICollectionConfigUpdate
) => {
  const modifiedMosaics = modifiedConfig.mosaics;
  await axios
    .get(`${STAC_URL}/collections/${collectionId}/configurations/mosaics`, {
      params: { "api-version": API_VERSION },
    })
    .then(async existingMosaics => {
      const putMosiacs: IMosaic[] = [],
        postMosaics: IMosaic[] = [],
        deleteMosaics: IMosaic[] = [];
      (modifiedMosaics as IMosaic[]).forEach(mosaic => {
        const key = mosaic.id;
        if (
          (existingMosaics.data as IMosaic[]).some(
            (mosaic: IMosaic) => mosaic.id === key
          )
        ) {
          putMosiacs.push(mosaic);
        } else {
          postMosaics.push(mosaic);
        }
      });
      (existingMosaics.data as IMosaic[]).forEach(mosaic => {
        const key = mosaic.id;
        if (
          !(modifiedMosaics as IMosaic[]).some(
            (mosaic: IMosaic) => mosaic.id === key
          )
        ) {
          deleteMosaics.push(mosaic);
        }
      });

      for (let i = 0; i < putMosiacs.length; i++) {
        await axios.put(
          `${STAC_URL}/collections/${collectionId}/configurations/mosaics/${putMosiacs[i].id}`,
          putMosiacs[i],
          { params: { "api-version": API_VERSION } }
        );
      }
      for (let i = 0; i < postMosaics.length; i++) {
        await axios.post(
          `${STAC_URL}/collections/${collectionId}/configurations/mosaics`,
          postMosaics[i],
          { params: { "api-version": API_VERSION } }
        );
      }
      for (let i = 0; i < deleteMosaics.length; i++) {
        await axios.delete(
          `${STAC_URL}/collections/${collectionId}/configurations/mosaics/${deleteMosaics[i].id}`,
          { params: { "api-version": API_VERSION } }
        );
      }
    });

  const resp = await axios.get(
    `${STAC_URL}/collections/${collectionId}/configurations/mosaics`,
    { params: { "api-version": API_VERSION } }
  );
  return resp.data;
};

/**
 * A function that accepts modified render options and performs the update.
 * @param collectionId  - The ID of the collection.
 */
export const updateRenderOptions = async (
  collectionId: string,
  modifiedConfig: ICollectionConfigUpdate
) => {
  const modifiedRenderOptions = modifiedConfig.renderOptions;
  await axios
    .get(`${STAC_URL}/collections/${collectionId}/configurations/render-options`, {
      params: { "api-version": API_VERSION },
    })
    .then(async existingRenderOptions => {
      const putOptions: IMosaicRenderOption[] = [],
        postOptions: IMosaicRenderOption[] = [],
        deleteOptions: IMosaicRenderOption[] = [];
      (modifiedRenderOptions as IMosaicRenderOption[]).forEach(mosaic => {
        const key = mosaic.id;
        if (
          (existingRenderOptions.data as IMosaicRenderOption[]).some(
            (mosaic: IMosaicRenderOption) => mosaic.id === key
          )
        ) {
          putOptions.push(mosaic);
        } else {
          postOptions.push(mosaic);
        }
      });
      (existingRenderOptions.data as IMosaicRenderOption[]).forEach(mosaic => {
        const key = mosaic.id;
        if (
          !(modifiedRenderOptions as IMosaicRenderOption[]).some(
            (mosaic: IMosaicRenderOption) => mosaic.id === key
          )
        ) {
          deleteOptions.push(mosaic);
        }
      });

      for (let i = 0; i < putOptions.length; i++) {
        await axios.put(
          `${STAC_URL}/collections/${collectionId}/configurations/render-options/${putOptions[i].id}`,
          putOptions[i],
          { params: { "api-version": API_VERSION } }
        );
      }
      for (let i = 0; i < postOptions.length; i++) {
        await axios.post(
          `${STAC_URL}/collections/${collectionId}/configurations/render-options`,
          postOptions[i],
          { params: { "api-version": API_VERSION } }
        );
      }
      for (let i = 0; i < deleteOptions.length; i++) {
        await axios.delete(
          `${STAC_URL}/collections/${collectionId}/configurations/render-options/${deleteOptions[i].id}`,
          { params: { "api-version": API_VERSION } }
        );
      }
    });

  const resp = await axios.get(
    `${STAC_URL}/collections/${collectionId}/configurations/render-options`,
    { params: { "api-version": API_VERSION } }
  );
  return resp.data;
};

/**
 * Updates the tile settings for a given collection.
 * @param collectionId - The ID of the collection.
 */
export const updateTileSettings = async ({
  collectionId,
  modifiedConfig,
}: {
  collectionId?: string;
  modifiedConfig?: ITileSettings;
}) => {
  const resp = await axios.put<ITileSettings>(
    `${STAC_URL}/collections/${collectionId}/configurations/tile-settings`,
    modifiedConfig,
    { params: { "api-version": API_VERSION } }
  );

  return resp.data;
};

/**
 * Updates the queryables for a given collection.
 * @param collectionId - The ID of the collection.
 */
export const updateQueryables = async (
  collectionId: string,
  modifiedConfig: ICollectionConfigUpdate
) => {
  const modifiedQueryables = modifiedConfig.queryables;
  await axios
    .get(`${STAC_URL}/collections/${collectionId}/queryables`, {
      params: { "api-version": API_VERSION },
    })
    .then(async response => {
      const existingQueryables: IQueryableDefinition[] = Object.keys(
        response.data.properties
      )
        .filter(
          (
            key // Do not update default queryables
          ) => !defaultQueryableNames.includes(key)
        )
        .map(key => {
          return {
            name: key,
            definition: response.data.properties[key],
          };
        });

      const putOptions: IQueryableDefinition[] = [],
        postOptions: IQueryableDefinition[] = [],
        deleteOptions: IQueryableDefinition[] = [];

      (modifiedQueryables as IQueryableDefinition[]).forEach(queryable => {
        const key = queryable.name;
        if (
          (existingQueryables as IQueryableDefinition[]).some(
            (q: IQueryableDefinition) => q.name === key
          )
        ) {
          putOptions.push(queryable);
        } else {
          postOptions.push(queryable);
        }
      });

      (existingQueryables as IQueryableDefinition[]).forEach(queryable => {
        const key = queryable.name;
        if (
          !(modifiedQueryables as IQueryableDefinition[]).some(
            (q: IQueryableDefinition) => q.name === key
          )
        ) {
          deleteOptions.push(queryable);
        }
      });

      for (let i = 0; i < putOptions.length; i++) {
        if (
          putOptions[i].name !== "id" &&
          putOptions[i].name !== "geometry" &&
          putOptions[i].name !== "datetime"
        ) {
          await axios.put(
            `${STAC_URL}/collections/${collectionId}/queryables/${putOptions[i].name}`,
            putOptions[i],
            { params: { "api-version": API_VERSION } }
          );
        }
      }

      await axios.post(
        `${STAC_URL}/collections/${collectionId}/queryables`,
        postOptions,
        { params: { "api-version": API_VERSION } }
      );

      for (let i = 0; i < deleteOptions.length; i++) {
        await axios.delete(
          `${STAC_URL}/collections/${collectionId}/queryables/${deleteOptions[i].name}`,
          { params: { "api-version": API_VERSION } }
        );
      }
    });

  const resp = await axios.get(
    `${STAC_URL}/collections/${collectionId}/configurations/render-options`,
    { params: { "api-version": API_VERSION } }
  );
  return resp.data;
};

export const useEditorPanelState = (initialState: PanelState) => {
  const [panelState, setState] = useState<PanelState>(initialState);

  const setPanelState = (newState: PanelState) => {
    setState(prevState => ({ ...prevState, ...newState }));
  };

  const handleCreateOpen = (collectionId?: string) => {
    setPanelState({
      isOpen: true,
      mode: "create",
      initialValue: initialEditorValue,
      collectionId: collectionId,
    });
  };

  const handleCreateWorkflowOpen = (collectionId?: string, ingestionId?: string) => {
    setPanelState({
      isOpen: true,
      mode: "create",
      initialValue: initialEditorValue,
      collectionId: collectionId,
      ingestionId: ingestionId,
    });
  };

  const handlePanelClose = () => {
    setPanelState({ isOpen: false });
  };

  const handleEditOpen = (collection: any) => {
    setPanelState({
      isOpen: true,
      mode: "update",
      initialValue: collection,
    });
  };

  return {
    panelState,
    setPanelState,
    handleCreateOpen,
    handleCreateWorkflowOpen,
    handleEditOpen,
    handlePanelClose,
  };
};

export const useDeleteItem = (item: IStacItem | undefined) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletionState, setDeletionState] = useState<
    "pending" | "success" | "error"
  >("success");
  const queryClient = useQueryClient();

  const mutation = useMutation(deleteItem, {
    onMutate: () => {
      setDeletionState("pending");
    },
    // On error, update the state
    onError: error => {
      setDeletionState("error");
    },
    onSuccess: (statusCheckUrl, item) => {
      // Use setInterval to poll every 5 seconds
      const interval = setInterval(() => {
        // Call the status check function with the url
        checkOperationStatus(statusCheckUrl)
          .then(data => {
            if (data.status === "Pending") {
              return;
            }
            // If the status check is successful, stop polling and update the state
            clearInterval(interval);
            if (data.status === "Failed") {
              throw new Error("Failed to delete item");
            }
            mutation.data = data.status;
            setDeletionState("success");
            // Invalidate any queries that depend on the deleted data
            queryClient.invalidateQueries([QUERY_KEYS.items, item.collection]);
            queryClient.removeQueries([QUERY_KEYS.items, item.collection]);
          })
          .catch(error => {
            // If the status check fails, stop polling and update the state
            clearInterval(interval);
            setDeletionState("error");
          });
      }, 5000);
    },
  });

  const handleConfirmDelete = async () => {
    if (item) {
      await mutation.mutateAsync(item);
    }
  };

  useEffect(() => {
    if (deletionState === "success") {
      setShowDeleteDialog(false);
    }
  }, [deletionState]);

  const handleCancelDelete = () => {
    setDeletionState("success");
    setShowDeleteDialog(false);
  };

  const deleteDialog = item && (
    <ConfirmDialog
      title="Delete item"
      onCancel={handleCancelDelete}
      onConfirm={handleConfirmDelete}
      confirmText={deletionState === "error" ? "Retry" : "Yes"}
      disableConfirm={deletionState === "pending"}
    >
      <Text block styles={itemTextStyles}>
        Are you sure you want to delete item <strong>{item.id}</strong>?
        <br />
        <b>
          {deletionState === "pending"
            ? "Deleting..."
            : deletionState === "error"
            ? "Failed"
            : ""}
        </b>
      </Text>
    </ConfirmDialog>
  );

  return { mutation, showDeleteDialog, setShowDeleteDialog, deleteDialog };
};

const deleteItem = async (item: IStacItem) => {
  const resp = await axios.delete(
    `${STAC_URL}/collections/${item.collection}/items/${item.id}`,
    { params: { "api-version": API_VERSION } }
  );
  if (resp.status === 202) {
    return resp.headers["location"];
  } else {
    throw new Error("Failed to delete item");
  }
};

/**
 * A function that checks the status of an operation.
 * @param location - The URL to check the status of the operation.
 * @returns The status of the operation.
 */
export const checkOperationStatus = async (location: string) => {
  try {
    // if location contains "/stac" replace with "/inma"
    // this is due to a bug in the API where the location returned is not correct
    // and needs to be replaced with the correct one
    // this should be removed once the API is fixed
    if (location.includes("/stac")) {
      location = location.replace("/stac", "/inma");
    }
    const resp = await axios.get(location);
    if (resp.status === 200) {
      return resp.data;
    } else if (resp.status === 404) {
      // Return a status of "Pending" to indicate that the operation is still in progress
      return { status: "Pending" };
    } else {
      throw new Error("Failed to poll for operation status");
    }
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      // Return a status of "Pending" to indicate that the operation is still in progress
      return { status: "Pending" };
    } else {
      throw new Error("Failed to poll for operation status");
    }
  }
};

const itemTextStyles = {
  root: {
    overflowWrap: "anywhere",
  },
};
